<template>
  <MRow
    v-show="!hideRow"
    class="elements-row"
    :class="{ 'elements-row--caption-grid': captionGrid }"
    noGutters
    align="center"
    :subgrid="captionGrid"
  >
    <template v-for="(item, itemIdx) in displayItems" :key="'elem-' + itemIdx">
      <MCol
        v-if="showLayoutPlaceholder(item)"
        :cols="item._isColspanPlaceholder ? 0 : (item._attributes?.cols ?? 100)"
        :width-mode="item._isColspanPlaceholder || item._isGridColumnSpacer ? undefined : item._attributes?.widthMode"
        :column-index="itemIdx"
        :class="[
          'elements-row-span-placeholder',
          { 'elements-row-colspan-placeholder': item._isColspanPlaceholder },
          { 'elements-row-grid-spacer': item._isGridColumnSpacer },
        ]"
        padding="0"
        :flex="item._isColspanPlaceholder ? '0 0 0' : item._isGridColumnSpacer ? '1 1 auto' : undefined"
        :overflow="item._isColspanPlaceholder || item._isGridColumnSpacer || item._isHiddenColumnSpacer ? 'hidden' : undefined"
      />
      <MElement
        v-else-if="item.element"
        :config="item"
        :form-path="formPath"
        :column-index="itemIdx"
        :label-hug-adjacent="labelHugByIndex[itemIdx]"
        :standalone-label-hug="standaloneHugByIndex[itemIdx]"
        :data-testid="resolveElementTestId(item)"
      />
      <MCol
        v-else
        :cols="item._attributes?.cols ?? 100"
        :width-mode="item._attributes?.widthMode"
        :column-index="itemIdx"
        class="elements-row-layout-cell"
        padding="0"
      />
    </template>
  </MRow>
</template>

<script setup lang="ts">
import { applyHiddenElementColumnSpacers } from '@/common/utils/hiddenElementColumnSpacer'
import { shouldLabelHugAdjacentField } from '@/common/utils/resolveLabelHugAdjacentField'
import { shouldStandaloneLabelHug } from '@/common/utils/resolveStandaloneLabelHug'
import MElement from '@/components/platform/elements/Element.vue'
import { ElementContextKey, FormProviderKey } from '@/components/platform/injection-keys'
import MCol from '@/components/ui/MCol.vue'
import MRow from '@/components/ui/MRow.vue'
import { computed, inject, onMounted } from 'vue'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { buildStableTestId } from '@/common/utils/testId'

interface Props {
  items: any[]
  captionGrid?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  captionGrid: false,
})
const formProvider = inject(FormProviderKey, null)

/** Одна ячейка + spacer'ы справа (заголовок секции) — на всю ширину строки, без ломания глобальной сетки. */
const baseDisplayItems = computed(() => {
  const items = props.items
  if (!Array.isArray(items) || items.length === 0) return items

  const isLayoutPlaceholder = (item: any) =>
    item?._isRowspanPlaceholder || item?._isColspanPlaceholder

  const elements = items.filter((item) => item?.element && !item?._isGridColumnSpacer && !isLayoutPlaceholder(item))
  const spacers = items.filter((item) => item?._isGridColumnSpacer)
  const onlySpacersBesidesElements =
    elements.length === 1 &&
    spacers.length > 0 &&
    elements.length + spacers.length + items.filter(isLayoutPlaceholder).length === items.length

  if (!onlySpacersBesidesElements) return items

  const el = elements[0]
  return [
    {
      ...el,
      _attributes: {
        ...(el._attributes || {}),
        cols: 100,
      },
    },
  ]
})

const rowLayout = computed(() =>
  applyHiddenElementColumnSpacers(
    baseDisplayItems.value,
    formProvider?.elementShellVisibleByElementId?.value,
  ),
)
const displayItems = computed(() => rowLayout.value.items)
const hideRow = computed(() => rowLayout.value.hideRow)

const labelHugByIndex = computed(() => {
  const map: Record<number, boolean> = {}
  displayItems.value.forEach((item, index) => {
    if (shouldLabelHugAdjacentField(displayItems.value, index)) {
      map[index] = true
    }
  })
  return map
})

const standaloneHugByIndex = computed(() => {
  const map: Record<number, boolean> = {}
  displayItems.value.forEach((item, index) => {
    if (shouldStandaloneLabelHug(displayItems.value, index)) {
      map[index] = true
    }
  })
  return map
})

const elementCount = computed(() => displayItems.value.filter((item: any) => item?.element).length)

onMounted(() => {
  if (elementCount.value >= 5) {
    startWorkflowMeasure('RowElements.onMounted', {
      elementCount: elementCount.value,
      formId: elementContext?.formId,
      panelId: elementContext?.panelId,
    })()
  }
})

// Получаем formPath из ElementContext
const elementContext = inject(ElementContextKey)

const showLayoutPlaceholder = (item: any) => {
  if (!item?._isRowspanPlaceholder && !item?._isGridColumnSpacer && !item?._isColspanPlaceholder && !item?._isHiddenColumnSpacer) {
    return false
  }
  // Colspan placeholders only pad the flex row. In the caption grid the cell spans itself.
  if (props.captionGrid && item._isColspanPlaceholder) return false
  return true
}
const formPath = computed(() => ({
  panelId: elementContext?.panelId,
  formId: elementContext?.formId,
}))

const resolveElementTestId = (item: any) =>
  buildStableTestId('element', [
    item?.element?._attributes?.id,
    item?.element?.name?._text,
  ])
</script>

<style scoped>
.elements-row {
  /* No column-gap: percent flex bases (25%+75%) + gap overflow 100% and clip
   * right-actions under panel overflow:hidden. Spacing comes from cell padding. */
  column-gap: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.elements-row > :deep(.m-col),
.elements-row > :deep(.element-container) {
  min-width: 0;
}

.elements-row-grid-spacer {
  min-width: 0;
  max-width: none;
}

.elements-row--caption-grid {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: center;
  width: 100%;
}

.elements-row--caption-grid > :deep(.element-type-label) {
  width: auto !important;
  min-width: max-content;
  max-width: 100%;
  justify-self: stretch;
}

.elements-row--caption-grid > :deep(.element-type-label) .element-simple-label {
  width: 100%;
  min-width: max-content;
}

.elements-row--caption-grid > :deep(.element-type-label) .element-simple-label-text {
  width: max-content;
  max-width: 100%;
}

.elements-row--caption-grid > :deep(.element-container:not(.element-type-label)) {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100%;
  justify-self: stretch;
}

.elements-row--caption-grid > :deep(.element-container--colspan) {
  grid-column: 1 / -1;
  width: 100% !important;
  min-width: 0 !important;
}
</style>
